import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';

function Section({ title, children }) {
  const theme = useTheme();
  return (
    <Box mb={4}>
      <Typography 
        variant="h5" 
        component="h2" 
        gutterBottom 
        sx={{ 
          color: theme.palette.primary.main,
          fontWeight: 'bold',
          mb: 2 
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function PrivacyPolicy() {
  const theme = useTheme();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            mb: 4 
          }}
        >
          Privacy Policy
        </Typography>
        
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Last updated: December 29, 2024
        </Typography>

        <Section title="Introduction">
          <Typography paragraph>
            Welcome to Drips Water ("we," "our," or "us"). We respect your privacy and are committed 
            to protecting your personal data. This privacy policy explains how we collect, use, 
            disclose, and safeguard your information when you use our Drips Water mobile 
            application ("App").
          </Typography>
        </Section>

        <Section title="Key Information at a Glance">
          <List>
            <ListItem>
              <ListItemText 
                primary="App Provider" 
                secondary="Aadi Manufacturing Private Limited" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Data Controller" 
                secondary="Aadi Manufacturing Private Limited" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Contact Email" 
                secondary="dripswater24@gmail.com" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Location" 
                secondary="Plot No - 952, At- Pathargadia, Chandaka, Khordha, Odisha, 754005, India" 
              />
            </ListItem>
          </List>
        </Section>

        <Section title="Data We Collect">
          <Typography variant="h6" gutterBottom>Personal Data</Typography>
          <List>
            {[
              'Email address',
              'First and last name',
              'Phone number',
              'Address details (including state, province, ZIP/postal code, city)',
              'Device information',
              'Usage data'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Automatically Collected Information</Typography>
          <List>
            {[
              'Device identifiers (including mobile device unique ID)',
              'IP address',
              'Operating system details',
              'Browser type and version',
              'Usage patterns and statistics',
              'App interaction data',
              'Technical diagnostic data'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="App Permissions">
          <Typography variant="h6" gutterBottom>Notification Permission</Typography>
          <Typography paragraph>
            <strong>Purpose:</strong> To send you important updates, alerts, and service-related notifications
          </Typography>
          <Typography paragraph>
            <strong>Data Collected:</strong> Device token for push notifications
          </Typography>
          <Typography paragraph>
            <strong>Control:</strong> You can manage notification settings through your device settings at any time
          </Typography>
          <Typography variant="subtitle1" gutterBottom>Usage:</Typography>
          <List>
            {[
              'Service updates and maintenance',
              'Account security alerts',
              'Important announcements',
              'Order status updates (if applicable)',
              'Promotional offers (which you can opt-out from)'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="How We Use Your Data">
          <List>
            {[
              'Providing and maintaining the App',
              'Account management',
              'Communication about updates and services',
              'Customer support',
              'Service improvement',
              'Security and fraud prevention',
              'Legal compliance'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Data Sharing and Disclosure">
          <Typography paragraph>We may share your information with:</Typography>
          <List>
            {[
              'Service providers and technical partners',
              'Business partners (with your consent)',
              'Legal authorities when required by law',
              'During business transactions (mergers, acquisitions)'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Data Security">
          <Typography paragraph>
            We implement appropriate technical and organizational measures to protect your personal data, including:
          </Typography>
          <List>
            {[
              'Encryption of data in transit',
              'Regular security assessments',
              'Access controls',
              'Secure data storage'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Your Privacy Rights">
          <Typography paragraph>You have the right to:</Typography>
          <List>
            {[
              'Access your personal data',
              'Correct inaccurate data',
              'Request deletion of your data',
              'Withdraw consent',
              'Object to processing',
              'Data portability'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Children's Privacy">
          <List>
            {[
              'Our App does not target users under 13 years old',
              'We do not knowingly collect data from children under 13',
              'Parents/guardians can contact us about children`s data'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Updates to Privacy Policy">
          <List>
            {[
              'We will notify you of material changes via email or App notification',
              'Updates will be posted on this page with a new "Last updated" date',
              'Continued use after changes implies acceptance'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Data Storage and International Transfers">
          <List>
            {[
              'Data is stored securely in accordance with industry standards',
              'International transfers comply with applicable data protection laws',
              'We implement appropriate safeguards for international data transfers'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Third-Party Services">
          <List>
            {[
              'Our App may contain links to third-party services',
              'We are not responsible for third-party privacy practices',
              'Review third-party privacy policies before providing information'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Cookies and Tracking">
          <Typography paragraph>We use technical cookies and similar technologies to:</Typography>
          <List>
            {[
              'Improve App functionality',
              'Analyze usage patterns',
              'Enhance user experience',
              'Maintain security'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Legal Basis for Processing">
          <Typography paragraph>We process your data based on:</Typography>
          <List>
            {[
              'Your consent',
              'Contract fulfillment',
              'Legal obligations',
              'Legitimate business interests'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Data Retention">
          <Typography paragraph>
            We retain personal data only as long as necessary. Retention periods are determined by:
          </Typography>
          <List>
            {[
              'Legal requirements',
              'Business needs',
              'User preferences'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Compliance with Data Protection Laws">
          <Typography paragraph>This privacy policy complies with:</Typography>
          <List>
            {[
              'Indian Information Technology Act, 2000',
              'Google Play Store Developer Program Policies',
              'Applicable international data protection regulations'
            ].map((item, index) => (
              <ListItem key={index}>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Section>

        <Section title="Contact Information">
          <Typography paragraph>
            For privacy-related inquiries:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Email" 
                secondary="dripswater24@gmail.com" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Address" 
                secondary="Plot No - 952, At- Pathargadia, Chandaka, Khordha, Odisha, 754005, India" 
              />
            </ListItem>
          </List>
        </Section>
      </Paper>
    </Container>
  );
}

export default PrivacyPolicy;